import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { timeAgo } from '@/lib/utils';
import { Project, statusLabels } from '@/types/project';
import DashboardNavbar from '@/components/feature/DashboardNavbar';
import EditProjectModal from '@/components/feature/EditProjectModal';
import ProjectLists from './components/ProjectLists';
import ProjectTasks from './components/ProjectTasks';
import ProjectFiles from './components/ProjectFiles';
import ProjectImages from './components/ProjectImages';
import ProjectDrawings from './components/ProjectDrawings';
import ProjectTeam from './components/ProjectTeam';


const tabs = [
  { id: 'lists', label: 'Listeler', icon: 'ri-list-check' },
  { id: 'tasks', label: 'İşler', icon: 'ri-task-line' },
  { id: 'files', label: 'Dosyalar', icon: 'ri-folder-line' },
  { id: 'images', label: 'Resimler', icon: 'ri-image-line' },
  { id: 'drawings', label: 'Çizimler', icon: 'ri-pencil-line' },
  { id: 'team', label: 'Kişiler', icon: 'ri-group-line' },
] as const;

type TabId = typeof tabs[number]['id'];



export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<TabId>('lists');
  const [showEditModal, setShowEditModal] = useState(false);

  // Fetch project from Supabase
  useEffect(() => {
    async function fetchProject() {
      if (!id) return;
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        .from('projects')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError || !data) {
        setError('Proje bulunamadı veya yüklenirken bir hata oluştu');
      } else {
        setProject(data as Project);
      }
      setLoading(false);
    }

    fetchProject();
  }, [id]);
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <DashboardNavbar />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-stone-300 border-t-amber-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-stone-50">
        <DashboardNavbar />
        <div className="flex flex-col items-center justify-center py-32">
          <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mb-4">
            <i className="ri-error-warning-line text-stone-400 text-2xl"></i>
          </div>
          <h2 className="text-lg font-semibold text-stone-700">{error || 'Proje bulunamadı'}</h2>
          <Link
            to="/dashboard"
            className="mt-4 text-amber-600 hover:text-amber-700 text-sm font-medium"
          >
            Dashboard'a dön
          </Link>
        </div>
      </div>
    );
  }

  const status = statusLabels[project.status];

  return (
    <div className="min-h-screen bg-stone-50">
      <DashboardNavbar />

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        {/* Back + Project Header */}
        <div className="mb-6">

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-stone-100">
                {project.thumbnail_url ? (
                  <img
                    src={project.thumbnail_url}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-folder-3-line text-stone-300 text-2xl"></i>
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-xl font-bold text-stone-800">{project.title}</h1>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                    {status.label}
                  </span>
                </div>
                {project.description && (
                  <p className="text-sm text-stone-500 max-w-lg">{project.description}</p>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-stone-600 bg-white border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-edit-line"></i>
                  Düzenle
                </button>
              </div>
              <div className="flex items-center gap-3 text-xs text-stone-400">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  Son güncelleme: {timeAgo(project.updated_at)}
                </span>
                <span className="text-stone-300">|</span>
                <span>
                  Oluşturuldu: {new Date(project.created_at).toLocaleDateString('tr-TR')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-stone-200 mb-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-stone-800 text-stone-800'
                    : 'border-transparent text-stone-500 hover:text-stone-700'
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {activeTab === 'lists' && <ProjectLists projectId={project.id} />}
          {activeTab === 'tasks' && <ProjectTasks projectId={project.id} />}
          {activeTab === 'files' && <ProjectFiles projectId={project.id} />}
          {activeTab === 'images' && <ProjectImages projectId={project.id} />}
          {activeTab === 'drawings' && <ProjectDrawings projectId={project.id} />}
          {activeTab === 'team' && <ProjectTeam projectId={project.id} />}
        </div>
      </main>

      {showEditModal && project && (
        <EditProjectModal
          project={project}
          onSave={(updated) => setProject(updated)}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </div>
  );
}
